import { formatDateTimeIST } from '@quickreport/domain';
import { qrSvg, type SlipPdfData } from '@quickreport/pdf';
import { pdfToImages } from '../../lib/desktop-print';
import type { LocalDb } from '../../local-db/client';
import { loadBrand } from '../../local-db/settings';

export type SlipPaper = 'thermal80' | 'a5';

const esc = (s: unknown) =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

export interface SlipData {
  lab: string;
  patient: string;
  ageSex: string;
  visitNo: string;
  receivedAt: string | null;
  tests: { name: string; labNo: string }[];
  track?: { url: string; code: string; svg: string };
  paper: SlipPaper;
}

/** The slip a patient takes from the counter. Test names of sensitive tests are never printed. */
export function slipHtml(d: SlipData): string {
  const thermal = d.paper === 'thermal80';
  const page = thermal ? '@page{size:80mm auto;margin:3mm}' : '@page{size:A5;margin:12mm}';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Counter slip</title><style>
${page}
*{box-sizing:border-box}
body{margin:0;color:#000;background:#fff;font:${thermal ? '11px' : '13px'}/1.35 "IBM Plex Sans",system-ui,sans-serif}
h1{font-size:${thermal ? '14px' : '18px'};margin:0 0 2px;text-align:${thermal ? 'center' : 'left'}}
.c{text-align:center}.m{color:#333}
hr{border:0;border-top:1px dashed #000;margin:6px 0}
table{width:100%;border-collapse:collapse}td{padding:1px 0;vertical-align:top}td.r{text-align:right;font-family:ui-monospace,Menlo,monospace}
.qr{width:${thermal ? '34mm' : '38mm'};height:${thermal ? '34mm' : '38mm'};margin:4px auto;display:block}
.qr svg{width:100%;height:100%}
.code{font-family:ui-monospace,Menlo,monospace;letter-spacing:1px}
</style></head><body>
<h1>${esc(d.lab)}</h1>
<div class="${thermal ? 'c ' : ''}m">Sample receipt</div><hr>
<div><b>${esc(d.patient)}</b>${d.ageSex ? ` · ${esc(d.ageSex)}` : ''}</div>
<div class="m">Visit ${esc(d.visitNo)}${d.receivedAt ? ` · ${esc(formatDateTimeIST(d.receivedAt))}` : ''}</div>
<hr>
<table>${d.tests.map((t) => `<tr><td>${esc(t.name)}</td><td class="r">${esc(t.labNo)}</td></tr>`).join('')}</table>
<hr>
${
  d.track
    ? `<div class="c"><b>Track your report</b><div class="m">Scan with your phone camera</div>
<div class="qr">${d.track.svg}</div>
<div class="code c">${esc(d.track.code)}</div><div class="m c">${esc(d.track.url.replace(/^https?:\/\//, ''))}</div></div>`
    : '<div class="m c">Please ask the counter for the status of your report.</div>'
}
</body></html>`;
}

/** What a slip says, read from this computer's copy of the visit. */
export async function loadSlipData(
  db: LocalDb,
  orgId: string,
  orgName: string,
  visitId: string,
  verifyBaseUrl: string,
  paper: SlipPaper,
): Promise<SlipData> {
  const visit = await db.query<{
    visit_no: string;
    track_code: string | null;
    received_at: string | null;
    collected_at: string | null;
  }>('select visit_no, track_code, received_at, collected_at from visits where id = $1', [visitId]);
  const v = visit.rows[0];
  if (!v) throw new Error('This visit is not on this computer');
  const reports = await db.query<{
    name: string;
    lab_no: string;
    sensitive: boolean;
    patient: { name?: string; salutation?: string; ageText?: string; sex?: string };
  }>(
    `select name, lab_no, sensitive, content->'patient' as patient from reports
      where visit_id = $1 and deleted_at is null order by lab_no`,
    [visitId],
  );
  const first = reports.rows[0]?.patient;
  const brand = await loadBrand(db, orgId, { labName: orgName });
  let track: SlipData['track'];
  if (v.track_code) {
    const url = `${verifyBaseUrl}/t/${v.track_code}`;
    track = {
      url,
      code: `${v.track_code.slice(0, 5)}-${v.track_code.slice(5)}`,
      svg: new TextDecoder().decode(await qrSvg(url)),
    };
  }
  return {
    lab: brand.labName || orgName,
    patient: [first?.salutation ? `${first.salutation}.` : '', first?.name ?? ''].join(' ').trim(),
    ageSex: [first?.ageText, first?.sex === 'M' ? 'Male' : first?.sex === 'F' ? 'Female' : '']
      .filter(Boolean)
      .join(' / '),
    visitNo: v.visit_no,
    receivedAt: v.received_at ?? v.collected_at,
    tests: reports.rows.map((r) => ({
      name: r.sensitive ? 'Confidential test' : r.name,
      labNo: r.lab_no,
    })),
    ...(track ? { track } : {}),
    paper,
  };
}

/** The slip as HTML, for printing from the desktop app. */
export async function buildSlip(
  db: LocalDb,
  orgId: string,
  orgName: string,
  visitId: string,
  verifyBaseUrl: string,
  paper: SlipPaper,
): Promise<string> {
  return slipHtml(await loadSlipData(db, orgId, orgName, visitId, verifyBaseUrl, paper));
}

export const SLIP_QR_PATH = '/assets/track-qr.svg';

/** The slip as the data its PDF template reads, with the QR image to map in beside it. */
export function slipPdfInput(d: SlipData): {
  data: SlipPdfData;
  assets: Record<string, Uint8Array>;
} {
  const data: SlipPdfData = {
    lab: d.lab,
    patient: d.patient,
    ageSex: d.ageSex,
    visitNo: d.visitNo,
    receivedAt: d.receivedAt ? formatDateTimeIST(d.receivedAt) : null,
    tests: d.tests,
    paper: d.paper,
    ...(d.track
      ? {
          track: {
            qrPath: SLIP_QR_PATH,
            code: d.track.code,
            url: d.track.url.replace(/^https?:\/\//, ''),
          },
        }
      : {}),
  };
  return {
    data,
    assets: d.track ? { [SLIP_QR_PATH]: new TextEncoder().encode(d.track.svg) } : {},
  };
}

/** Print a document from a hidden frame so the app itself never changes. */
export function printHtml(html: string) {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  frame.srcdoc = html;
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    setTimeout(() => frame.remove(), 60_000);
  };
  document.body.appendChild(frame);
}

const FRAME_ID = 'labview-print-frame';

/** The slip's pages as pictures on sheets of the right size, so no PDF viewer is needed. */
export function slipSheetsHtml(images: string[], paper: SlipPaper): string {
  const thermal = paper === 'thermal80';
  const size = thermal ? '80mm auto' : 'A5';
  const w = thermal ? '80mm' : '148mm';
  const pages = images.map((src) => `<img src="${src}" alt="">`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Counter slip</title><style>
@page{size:${size};margin:0}*{box-sizing:border-box}html,body{margin:0;background:#fff}
img{display:block;width:${w};height:auto;break-after:page}img:last-child{break-after:auto}
</style></head><body>${pages}</body></html>`;
}

/**
 * Print the slip from the browser. One frame at a time, taken away before a new one is made (an
 * old frame left behind is what made a second print do nothing), at a real size, because some
 * browsers show "loading preview" forever for a 0 by 0 frame. The slip goes in as pictures, so
 * no PDF viewer has to load. If printing does not start in a few seconds, or the device cannot
 * print from a frame (phones, tablets), the PDF opens in its own tab to print or save.
 */
export async function printSlipInBrowser(
  pdf: Uint8Array,
  pdfUrl: string,
  paper: SlipPaper,
): Promise<void> {
  document.getElementById(FRAME_ID)?.remove();
  const openPdf = () => void window.open(pdfUrl, '_blank', 'noopener');
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return openPdf();
  await printHtmlInFrame(slipSheetsHtml(await pdfToImages(pdf), paper), openPdf);
}

/**
 * Print finished page pictures from one off-screen frame, and call `fallback` if printing does
 * not start within a few seconds (or the browser refuses to print from a frame).
 */
export async function printHtmlInFrame(html: string, fallback: () => void): Promise<void> {
  document.getElementById(FRAME_ID)?.remove();
  await new Promise<void>((resolve) => {
    const frame = document.createElement('iframe');
    frame.id = FRAME_ID;
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText =
      'position:fixed;left:-10000px;top:0;width:794px;height:1123px;border:0;visibility:hidden';
    let finished = false;
    let started = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(giveUp);
      frame.remove();
      resolve();
    };
    const giveUp = setTimeout(() => {
      if (!started) fallback();
      finish();
    }, 8000);
    frame.onload = async () => {
      const w = frame.contentWindow;
      if (!w) return finish();
      try {
        // Wait for the pictures, or a browser prints a blank page.
        await Promise.all(
          [...(frame.contentDocument?.images ?? [])].map((i) => i.decode().catch(() => undefined)),
        );
        w.onafterprint = finish;
        started = true;
        w.focus();
        w.print();
        // Some browsers never say that printing has ended.
        clearTimeout(giveUp);
        setTimeout(finish, 120_000);
      } catch {
        fallback();
        finish();
      }
    };
    frame.srcdoc = html;
    document.body.appendChild(frame);
  });
}
